// src/features/form/SubmissionsTab.tsx
// Submissions table built from the form's own field schema — one column per
// field, plus status/submitted-at. Row -> detail dialog with review/delete.
// Toolbar -> export (csv/json/xlsx).

import { useEffect, useState } from 'react';
import { useI18n } from '@react/hooks';
import { Alert, Badge, Button, Table, TableBody, TableCell, TableEmpty, TableHead, TableHeader, TableRow } from '@react/components';
import { Select } from '@/components/Select';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { formApi } from '@core/api';
import type { Form, FormSubmission, FormSubmissionStatus } from '@core/types/form';
import { FormModalShell } from './FormModalShell';
import { NON_ANSWER_FIELD_TYPES, SUBMISSION_STATUSES, errMessage, submissionStatusBadgeVariant } from './helpers';

export interface SubmissionsTabProps {
  form: Form;
}

function answerText(sub: FormSubmission, fieldId: string): string {
  const a = sub.answers.find((x) => x.field_id === fieldId);
  if (!a) return '';
  if (a.values.length) return a.values.join(', ');
  return a.value;
}

export function SubmissionsTab({ form }: SubmissionsTabProps) {
  const { t, locale } = useI18n();
  const formId = form.entity?.id ?? '';
  const columns = (form.pages[0]?.fields ?? []).filter((f) => !NON_ANSWER_FIELD_TYPES.includes(f.field_type ?? 'FORM_FIELD_TYPE_UNSPECIFIED'));

  const [submissions, setSubmissions] = useState<FormSubmission[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<'' | FormSubmissionStatus>('');
  const [selected, setSelected] = useState<FormSubmission | null>(null);
  const [deleting, setDeleting] = useState<FormSubmission | null>(null);
  const [busy, setBusy] = useState(false);
  const [exporting, setExporting] = useState(false);

  const load = () => {
    setIsLoading(true);
    setError(null);
    formApi
      .ListFormSubmissions(formId)
      .then((res) => setSubmissions(res.items?.items ?? []))
      .catch((e) => setError(errMessage(e, t('form.submissions.loadError'))))
      .finally(() => setIsLoading(false));
  };

  const openDetail = async (sub: FormSubmission) => {
    setSelected(sub);
    if (!sub.entity?.id) return;
    try {
      const res = await formApi.GetFormSubmission(sub.entity.id);
      if (res.submission) setSelected(res.submission);
    } catch {
      // keep the list payload as a fallback
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formId]);

  const filtered = statusFilter ? submissions.filter((s) => s.status === statusFilter) : submissions;

  const review = async (status: FormSubmissionStatus) => {
    if (!selected?.entity?.id) return;
    setBusy(true);
    setError(null);
    try {
      const res = await formApi.ReviewFormSubmission(selected.entity.id, { submission: selected.entity, status });
      if (res.submission) setSelected(res.submission);
      load();
    } catch (e) {
      setError(errMessage(e, t('form.submissions.reviewError')));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!deleting?.entity?.id) return;
    setBusy(true);
    try {
      await formApi.DeleteFormSubmission(deleting.entity.id);
      setDeleting(null);
      setSelected(null);
      load();
    } catch (e) {
      setError(errMessage(e, t('form.submissions.deleteError')));
      setDeleting(null);
    } finally {
      setBusy(false);
    }
  };

  const doExport = async (format: string) => {
    setExporting(true);
    setError(null);
    try {
      const res = await formApi.ExportFormSubmissions(formId, { format });
      const bytes = Uint8Array.from(atob(res.data), (c) => c.charCodeAt(0));
      const blob = new Blob([bytes], { type: res.content_type || 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = res.filename || `submissions.${format}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(errMessage(e, t('form.submissions.exportError')));
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="w-56">
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as FormSubmissionStatus | '')}
            options={[
              { value: '', label: t('form.submissions.allStatuses') },
              ...SUBMISSION_STATUSES.map((s) => ({ value: s, label: t(`form.submissionStatus.${s.replace('FORM_SUBMISSION_STATUS_', '').toLowerCase()}`) })),
            ]}
            aria-label={t('form.submissions.filterStatus')}
          />
        </div>
        <div className="flex gap-1.5">
          <Button size="sm" variant="secondary" isLoading={exporting} onClick={() => doExport('csv')}>
            {t('form.submissions.exportCsv')}
          </Button>
          <Button size="sm" variant="secondary" isLoading={exporting} onClick={() => doExport('xlsx')}>
            {t('form.submissions.exportXlsx')}
          </Button>
        </div>
      </div>

      {error && <Alert variant="error">{error}</Alert>}

      {isLoading && <div className="h-32 animate-pulse rounded-md bg-muted" aria-busy="true" />}

      {!isLoading && (
        <div className="overflow-x-auto rounded-md border border-border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('form.submissions.submitted')}</TableHead>
                <TableHead>{t('form.submissions.status')}</TableHead>
                {columns.map((c) => (
                  <TableHead key={c.entity?.id ?? c.label}>{c.label}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 && <TableEmpty colSpan={columns.length + 2}>{t('form.submissions.empty')}</TableEmpty>}
              {filtered.map((s) => (
                <TableRow key={s.entity?.id} onClick={() => openDetail(s)} className="cursor-pointer">
                  <TableCell>{s.submitted_at ? new Date(s.submitted_at).toLocaleString(locale) : '—'}</TableCell>
                  <TableCell>
                    <Badge variant={submissionStatusBadgeVariant(s.status)}>
                      {t(`form.submissionStatus.${(s.status ?? 'FORM_SUBMISSION_STATUS_SUBMITTED').replace('FORM_SUBMISSION_STATUS_', '').toLowerCase()}`)}
                    </Badge>
                  </TableCell>
                  {columns.map((c) => (
                    <TableCell key={c.entity?.id ?? c.label} className="max-w-[220px] truncate">
                      {answerText(s, c.entity?.id ?? '')}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <FormModalShell open={!!selected} title={t('form.submissions.detailTitle')} onClose={() => setSelected(null)}>
        {selected && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Badge variant={submissionStatusBadgeVariant(selected.status)}>
                {t(`form.submissionStatus.${(selected.status ?? 'FORM_SUBMISSION_STATUS_SUBMITTED').replace('FORM_SUBMISSION_STATUS_', '').toLowerCase()}`)}
              </Badge>
              <span className="text-xs text-muted-foreground">
                {selected.submitted_at ? new Date(selected.submitted_at).toLocaleString(locale) : ''}
              </span>
            </div>
            <div className="space-y-2">
              {columns.map((c) => (
                <div key={c.entity?.id ?? c.label}>
                  <p className="text-xs font-medium text-muted-foreground">{c.label}</p>
                  <p className="text-sm text-foreground">{answerText(selected, c.entity?.id ?? '') || '—'}</p>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between gap-2 border-t border-border pt-3">
              <Button size="sm" variant="danger" onClick={() => setDeleting(selected)}>
                {t('common.delete')}
              </Button>
              <div className="flex gap-1.5">
                <Button size="sm" variant="secondary" isLoading={busy} onClick={() => review('FORM_SUBMISSION_STATUS_REJECTED')}>
                  {t('form.submissions.reject')}
                </Button>
                <Button size="sm" isLoading={busy} onClick={() => review('FORM_SUBMISSION_STATUS_APPROVED')}>
                  {t('form.submissions.approve')}
                </Button>
              </div>
            </div>
          </div>
        )}
      </FormModalShell>

      <ConfirmDialog
        open={!!deleting}
        title={t('form.submissions.deleteTitle')}
        description={t('form.submissions.deleteDescription')}
        destructive
        isConfirming={busy}
        onCancel={() => setDeleting(null)}
        onConfirm={remove}
      />
    </div>
  );
}
